// 角色資訊結構
export interface RoleInfo {
  type: string;          // "admin", "vendor", "channel_admin", "user"
  role: string;          // 具體角色
  vendorId?: string;     // 店家 ID（僅 vendor 類型）
  vendorName?: string;   // 店家名稱（僅 vendor 類型）
}

export interface JWTPayload {
  sub: string;           // 使用者ID
  roles: RoleInfo[];     // 所有角色
  channelId?: string;    // 主辦單位ID
  exp: number;           // 過期時間
  iat: number;           // 簽發時間
}

export interface AuthUser {
  id: string;
  name: string;
  username?: string; // 活動平台用戶名
  email?: string; // 電子郵件
  roles: RoleInfo[];     // 所有角色（來自JWT）
  currentRole: RoleInfo; // 當前選擇的角色（前端管理）
  currentVendorId?: string; // 當前選擇的店家ID（vendor角色時使用）
  channelId?: string;
  avatar?: string; // 頭像 URL
}

export interface MockTokenRequest {
  role: string;
  userId?: string;
  vendorId?: string;
  channelId?: string;
}

export interface MockTokenResponse {
  success: boolean;
  code: string;
  message: string;
  data: {
    token: string;
    user: AuthUser;
    expiresIn: number;
  };
}

export interface RoleUser {
  user_id: string;
  user_name: string;
  line_id?: string;
  role?: string;
  brand_name?: string;
  vendor_id?: string;
  status?: string;
  created_at?: string;
}

export type UserRole = 
  | 'super_admin'        // 最高管理者
  | 'super_admin_co'     // 最高管理者共管人員
  | 'vendor'             // 店家
  | 'vendor_co'          // 店家共管人員
  | 'channel_admin'      // 主辦單位
  | 'user';              // 一般使用者

export interface RoleDefinition {
  role: UserRole;
  name: string;
  description: string;
}

export const ROLE_INFO: Record<UserRole, RoleDefinition> = {
  super_admin: {
    role: 'super_admin',
    name: '最高管理者',
    description: '擁有系統內所有功能的完整權限'
  },
  super_admin_co: {
    role: 'super_admin_co',
    name: '最高管理者共管人員',
    description: '協助最高管理者管理主辦單位資訊'
  },
  channel_admin: {
    role: 'channel_admin',
    name: '主辦單位',
    description: '管理店家審核、商品上架審核及相關報表'
  },
  vendor: {
    role: 'vendor',
    name: '店家',
    description: '管理商品、設定共管人員及查看報表'
  },
  vendor_co: {
    role: 'vendor_co',
    name: '店家共管人員',
    description: '協助店家管理商品及查看報表'
  },
  user: {
    role: 'user',
    name: '一般使用者',
    description: '使用點數兌換功能及申請店家'
  }
};

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  roles: UserRole[];
  // 自定義顯示條件檢查函數（可選）
  shouldDisplay?: (user: AuthUser) => boolean;
}

export interface MenuSection {
  id: string;
  title: string;
  roles: UserRole[];
  items: MenuItem[];
}

export const MENU_CONFIG: MenuSection[] = [
  {
    id: 'vendor',
    title: '店家管理',
    roles: ['vendor', 'vendor_co'],
    items: [
      {
        id: 'vendor_products',
        label: '商品管理',
        icon: 'package',
        route: '/admin/vendor/products',
        roles: ['vendor', 'vendor_co']
      },
      {
        id: 'vendor_reports',
        label: '點數兌換報表',
        icon: 'trending-up',
        route: '/admin/vendor/reports/exchange',
        roles: ['vendor', 'vendor_co']
      },
      {
        id: 'vendor_staff',
        label: '共管人員設定',
        icon: 'users',
        route: '/admin/vendor/staff',
        roles: ['vendor'] // 只有店家本身可以設定共管人員
      },
      {
        id: 'vendor_profile',
        label: '基本資料設定',
        icon: 'user',
        route: '/admin/vendor/profile',
        roles: ['vendor', 'vendor_co']
      }
    ]
  },
  {
    id: 'channel',
    title: '主辦單位管理',
    roles: ['channel_admin'],
    items: [
      {
        id: 'channel_product_review',
        label: '商品審核管理',
        icon: 'clipboard-check',
        route: '/admin/channel/products/review',
        roles: ['channel_admin']
      },
      {
        id: 'channel_vendors',
        label: '店家管理',
        icon: 'store',
        route: '/admin/channel/vendors',
        roles: ['channel_admin']
      },
      {
        id: 'channel_exchange_reports',
        label: '點數兌換報表',
        icon: 'file-text',
        route: '/admin/channel/reports/exchange',
        roles: ['channel_admin']
      },
      {
        id: 'channel_revenue_reports',
        label: '商家營收報表',
        icon: 'bar-chart-3',
        route: '/admin/channel/reports/revenue',
        roles: ['channel_admin']
      }
    ]
  },
  {
    id: 'super_admin',
    title: '最高管理者',
    roles: ['super_admin', 'super_admin_co'],
    items: [
      {
        id: 'super_admin_channels',
        label: '主辦單位管理',
        icon: 'building',
        route: '/admin/super-admin/channels',
        roles: ['super_admin', 'super_admin_co']
      },
      {
        id: 'super_admin_staff',
        label: '共管人員設定',
        icon: 'users',
        route: '/admin/super-admin/staff',
        roles: ['super_admin'] // 只有最高管理者可以設定共管人員
      },
      {
        id: 'super_admin_logs',
        label: '日誌管理',
        icon: 'file-text',
        route: '/admin/super-admin/logs',
        roles: ['super_admin'] // 只有最高管理者可以查看日誌
      }
    ]
  },
  {
    id: 'user',
    title: '使用者功能',
    roles: ['user', 'super_admin', 'super_admin_co', 'channel_admin'],
    items: [
      {
        id: 'vendor_application',
        label: '申請成為店家',
        icon: 'store',
        route: '/admin/vendor-application',
        roles: ['user', 'super_admin', 'super_admin_co', 'channel_admin'],
        // 特殊邏輯：如果用戶已經擁有店家角色，則不顯示此選單
        shouldDisplay: (user: AuthUser) => {
          const vendorRoles = ['vendor', 'vendor_co'];
          return !user.roles.some(role => vendorRoles.includes(role.role));
        }
      }
    ]
  }
];